type WrapperConflictParams = { goal: string; accountA: string; accountB: string; wrapper: string };

export const T = {
  en_GB: {
    // QL-212 — a global-page risk assessment needs the flow's risk model.
    riskModelNotConfigured: "Risk model not configured for this flow.",
    // Dialog
    submitFlow: "Submit Flow",
    submitConfirmDescription:
      "Are you sure you want to submit this flow? You won't be able to edit it after submission.",
    submit: "Submit",
    cancel: "Cancel",
    // Alerts
    success: "Success",
    error: "Error",
    progressSaved: "Saved",
    failedToSaveProgress: "Failed to save progress",
    failedToSaveValues: "Failed to save values",
    failedToCompleteFlow: "Failed to complete flow",
    failedToCancelAdvice: "Failed to cancel the advice",
    failedToReopen: "Failed to reopen the flow",
    cancelAdviceTitle: "Cancel advice",
    cancelAdviceDescription:
      "The flow is locked as cancelled (e.g. the advice cannot be given). You can still generate a PDF documenting the decision, and reopen the flow later.",
    cancelAdviceConfirm: "Cancel advice",
    cancelReasonLabel: "Cancellation reason",
    cancelReasonPlaceholder: "Explain why the advice is cancelled…",
    // Page state
    noInternalPagesConfigured: "This flow has no internal pages configured.",
    loadingGoalInfo: "Loading goal information...",
    // Component messages
    fieldNotFound: "Field not found",
    noProductTypesConfigured: "No product types configured for this collection.",
    // QL-140 — per-type template: a model may serve any subset of the three
    // client types, so the message names the type that has no fields.
    financialSituationNotAvailable: (type: string) =>
      `Financial Situation is not available for ${type} clients in this flow's financial situation model.`,
    clientTypeWordCompany: "company",
    clientTypeWordGroup: "group",
    // Snapshot regeneration
    snapshotsStale:
      "Reference data (products, portfolios, asset classes or risk settings) changed since this flow was last opened. Snapshots must be refreshed before the flow can be closed or a PDF can be generated.",
    snapshotMissing:
      "Portfolio information is missing for one or more goals. Snapshots must be refreshed before the flow can be closed or a PDF can be generated.",
    snapshotsRegenerating:
      "Updating snapshots after a change to reference data or portfolios. This usually takes a few seconds; the flow refreshes by itself when it is done.",
    // QL-493 — the flow template cannot enforce a risk bound its risk model sets.
    templateCannotComplete:
      "This flow cannot be completed: the flow template cannot enforce the risk model's risk bounds. Ask a flow designer to fix the template.",
    configErrorVolatilityChart:
      "The risk model sets a max volatility, but the template has no Volatility Chart on a goal page.",
    configErrorEquityShareChart:
      "The risk model sets a max equity share, but the template has no Equity Share Chart on a goal page.",
    configErrorMandate:
      "The portfolio collection has mandates enabled, but the template is missing the Mandate component (or the risk assessment) that enforces them.",
    configErrorRiskAssessment:
      "The risk model enforces risk bounds, but the template has no Risk Assessment to score the goals by.",
    configErrorCollectionSetting:
      "The risk model enforces risk bounds, but the portfolio collection has 'Requires asset allocation' off, so the charts that check them cannot be placed.",
    // QL-576 — a follow-up whose relinked accounts would share a wrapper type on
    // one successor goal (bank data drifted after creation).
    followupConflictTitle: "This follow-up cannot be completed as it stands:",
    followupWrapperConflictLine: (p: WrapperConflictParams) =>
      `In goal "${p.goal}", the accounts "${p.accountA}" and "${p.accountB}" now have the same wrapper type (${p.wrapper}); a goal can hold only one account per wrapper type.`,
    followupWrapperNone: "none",
    followupWrapperConflictRemedy:
      "Cancel this follow-up and create it again with these accounts in separate goals (or, if the bank linked an extra account to the goal, have it moved away).",
    refreshSnapshots: "Refresh snapshots",
    snapshotsRefreshFailed: "Failed to refresh snapshots",
    // QL-212 — client-level risk assessment autosave
    failedToSaveRiskAssessment: "Failed to save the risk assessment",
    riskMatrixGap:
      "The risk model has no score for the selected time horizon and risk level. Nothing was saved.",
    // Goal names (used in flow language, not UI language)
    investmentGoal: "Investment Goal",
    // QL-264 — standalone launch: the process finished on submit
    redirectingBack: (host: string) => `Redirecting you back to ${host}…`,
  },
  nb_NO: {
    // QL-212 — a global-page risk assessment needs the flow's risk model.
    riskModelNotConfigured: "Risikomodell er ikke konfigurert for denne flyten.",
    // Dialog
    submitFlow: "Send inn flyt",
    submitConfirmDescription:
      "Er du sikker på at du vil sende inn denne flyten? Du vil ikke kunne redigere den etter innsending.",
    submit: "Send inn",
    cancel: "Avbryt",
    // Alerts
    success: "Suksess",
    error: "Feil",
    progressSaved: "Lagret",
    failedToSaveProgress: "Kunne ikke lagre fremdrift",
    failedToSaveValues: "Kunne ikke lagre verdier",
    failedToCompleteFlow: "Kunne ikke fullføre flyt",
    failedToCancelAdvice: "Kunne ikke kansellere rådet",
    failedToReopen: "Kunne ikke gjenåpne flyten",
    cancelAdviceTitle: "Kanseller råd",
    cancelAdviceDescription:
      "Flyten låses som kansellert (f.eks. når råd ikke kan gis). Du kan fortsatt generere en PDF som dokumenterer beslutningen, og gjenåpne flyten senere.",
    cancelAdviceConfirm: "Kanseller råd",
    cancelReasonLabel: "Kanselleringsårsak",
    cancelReasonPlaceholder: "Forklar hvorfor rådet kanselleres…",
    // Page state
    noInternalPagesConfigured: "Denne flyten har ingen interne sider konfigurert.",
    loadingGoalInfo: "Laster målinformasjon...",
    // Component messages
    fieldNotFound: "Felt ikke funnet",
    noProductTypesConfigured: "Ingen produkttyper konfigurert for denne samlingen.",
    financialSituationNotAvailable: (type: string) =>
      `Økonomisk situasjon er ikke tilgjengelig for ${type} i denne flytens modell for økonomisk situasjon.`,
    clientTypeWordCompany: "bedriftskunder",
    clientTypeWordGroup: "gruppekunder",
    // Snapshot regeneration
    snapshotsStale:
      "Referansedata (produkter, porteføljer, aktivaklasser eller risikoinnstillinger) har endret seg siden flyten sist ble åpnet. Øyeblikksbilder må oppdateres før flyten kan lukkes eller en PDF genereres.",
    snapshotMissing:
      "Porteføljeinformasjon mangler for ett eller flere mål. Øyeblikksbilder må oppdateres før flyten kan lukkes eller en PDF genereres.",
    snapshotsRegenerating:
      "Oppdaterer øyeblikksbilder etter en endring i referansedata eller porteføljer. Dette tar vanligvis noen sekunder; flyten oppdateres av seg selv når det er ferdig.",
    templateCannotComplete:
      "Denne flyten kan ikke fullføres: flytmalen kan ikke håndheve risikomodellens risikogrenser. Be en flytdesigner rette malen.",
    configErrorVolatilityChart:
      "Risikomodellen setter en maks volatilitet, men malen har ingen volatilitetsgraf på en målside.",
    configErrorEquityShareChart:
      "Risikomodellen setter en maks aksjeandel, men malen har ingen aksjeandelsgraf på en målside.",
    configErrorMandate:
      "Porteføljesamlingen har mandater aktivert, men malen mangler mandatkomponenten (eller risikovurderingen) som håndhever dem.",
    configErrorRiskAssessment:
      "Risikomodellen håndhever risikogrenser, men malen har ingen risikovurdering å score målene med.",
    configErrorCollectionSetting:
      "Risikomodellen håndhever risikogrenser, men porteføljesamlingen har «Krever aktivaallokering» av, så grafene som sjekker dem kan ikke plasseres.",
    // QL-576 — en oppfølging der de omkoblede kontoene får samme innpakningstype
    // på samme mål (bankdata har driftet etter opprettelse).
    followupConflictTitle: "Denne oppfølgingen kan ikke fullføres slik den er nå:",
    followupWrapperConflictLine: (p: WrapperConflictParams) =>
      `I målet «${p.goal}» har kontoene «${p.accountA}» og «${p.accountB}» nå samme innpakningstype (${p.wrapper}); et mål kan bare ha én konto per innpakningstype.`,
    followupWrapperNone: "ingen",
    followupWrapperConflictRemedy:
      "Kanseller denne oppfølgingen og opprett den på nytt med disse kontoene i hver sitt mål (eller, hvis banken har knyttet en ekstra konto til målet, få den flyttet bort).",
    refreshSnapshots: "Oppdater øyeblikksbilder",
    snapshotsRefreshFailed: "Kunne ikke oppdatere øyeblikksbilder",
    // QL-212 — client-level risk assessment autosave
    failedToSaveRiskAssessment: "Kunne ikke lagre risikovurderingen",
    riskMatrixGap:
      "Risikomodellen har ingen score for valgt tidshorisont og risikonivå. Ingenting ble lagret.",
    // Goal names (used in flow language, not UI language)
    investmentGoal: "Investeringsmål",
    // QL-264 — standalone launch: the process finished on submit
    redirectingBack: (host: string) => `Sender deg tilbake til ${host}…`,
  },
} as const;
